import React, { ForwardedRef, useEffect, useMemo } from "react";
import { useMeasure } from "react-use";
import { useLocation } from "react-router-dom";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { B2C_SITE_ORIGIN } from "@danishagro/shared/src/constants/urlRoot.constants";
import { getLoginLink } from "@danishagro/shared/src/helpers/getLoginLink.helper";
import { useBasket } from "@danishagro/shared/src/contexts/basket/basket.context";
import { usePrices } from "@danishagro/shared/src/hooks/usePrices.hook";
import { useScreen } from "../../../../contexts/screen.context";
import { SiteHeaderPopUpType, useSiteHeader } from "../../../../contexts/siteHeader.context";
import { DA_Icon, DA_IconNames } from "../../../atoms/Icon/Icon.component";
import { DA_BasicLink } from "../../../atoms/BasicLink/BasicLink.component";
import { DA_Badge } from "../Badge/Badge.component";
import { useTranslations } from "../../../../contexts/translations/translations.context";
import { getRelativeRootOnLocalhost } from "../../../../helpers/getRelativeUrlOnLocalhost.helper";
import { useAppData } from "../../../../contexts/appData.context";
import S from "./ServiceButtonsB2c.module.scss";

export const DA_ServiceButtonsB2c = React.forwardRef((_, ref: ForwardedRef<HTMLDivElement>) => {
    const { pathname } = useLocation();
    const { getDictionaryString } = useTranslations();
    const { cvrAndCustomerNumbers, currentSite } = useAppData();
    const { isMobileOrTablet } = useScreen();
    const { formatPrice } = usePrices();
    const {
        setCartButtonMeasure,
        setFarmInTimeButtonMeasure,
        setAccountButtonMeasure,
        showMiniCart,
        showAccountPicker,
        togglePopUp,
    } = useSiteHeader();
    const { orderLines, isLoadingBasket, priceData, isFirstAdd } = useBasket();

    const [cartButtonRef, cartButtonRect] = useMeasure<HTMLButtonElement>();

    const totalQuantity = useMemo(() => {
        if (orderLines?.[0]) {
            return orderLines.reduce((total, line) => total + line.quantity, 0);
        }

        return 0;
    }, [orderLines]);

    const favoritesListUrl = `${getRelativeRootOnLocalhost(B2C_SITE_ORIGIN)}/favorites`;

    const hasOpenMenu = showMiniCart || showAccountPicker;

    useEffect(() => {
        setCartButtonMeasure(cartButtonRect);

        // use cartbutton to calc account popup
        setAccountButtonMeasure(cartButtonRect);
    }, [cartButtonRect, setCartButtonMeasure, setFarmInTimeButtonMeasure, setAccountButtonMeasure]);

    return (
        <div className={S.serviceButtons} ref={ref}>
            {/** Favourties list */}
            <DA_BasicLink
                href={favoritesListUrl}
                target="_self"
                className={cn(
                    S.serviceButton,
                    pathname === favoritesListUrl && !hasOpenMenu && S.active
                )}
                ariaLabel={getDictionaryString("accessibility.favoritList.link")}
            >
                <span className={S.service}>
                    <DA_Icon name={DA_IconNames.HeartOutline} className={S.icon} />
                </span>
            </DA_BasicLink>

            {/** Account: Logged Out */}
            {!globalThis.isAuthenticated && (
                <DA_BasicLink
                    href={getLoginLink(currentSite)}
                    target="_self"
                    className={cn(S.serviceButton, S.user, showAccountPicker && S.active)}
                    ariaLabel={getDictionaryString("accessibility.login.link")}
                >
                    <span className={cn(S.service, S.user)}>
                        <div className={S.buttonIconWrapper}>
                            <DA_Icon name={DA_IconNames.UserLoggedOut} className={S.icon} />
                        </div>
                    </span>
                </DA_BasicLink>
            )}

            {/** Account: Logged In */}
            {!isMobileOrTablet && globalThis.isAuthenticated && cvrAndCustomerNumbers ? (
                <button
                    className={cn(S.serviceButton, showAccountPicker && S.active)}
                    type="button"
                    onClick={() => togglePopUp(SiteHeaderPopUpType.AccountPicker)}
                    title={getDictionaryString("accessibility.accountPicker")}
                >
                    <span className={cn(S.service, S.user)}>
                        <div className={S.buttonIconWrapper}>
                            <DA_Icon name={DA_IconNames.UserLoggedIn} className={S.icon} />
                        </div>
                    </span>
                </button>
            ) : null}

            {/** Cart */}
            <button
                type="button"
                className={cn(
                    S.cartButton,
                    priceData && orderLines?.length > 0 && S.hasItems,
                    isFirstAdd && S.animateButton
                )}
                onClick={() => togglePopUp(SiteHeaderPopUpType.MiniCart)}
                ref={cartButtonRef}
                aria-label={getDictionaryString("accessibilityButtonCart")}
            >
                <span className={cn(S.serviceButton, S.cart, showMiniCart && S.active)}>
                    <DA_Icon name={DA_IconNames.Cart} className={S.icon} />

                    <DA_Badge
                        size="small"
                        text={totalQuantity > 99 ? "99+" : totalQuantity}
                        className={cn(S.buttonBadge, isFirstAdd && S.animateBadge)}
                        showSpinner={isLoadingBasket}
                    />
                </span>

                <span className={S.cartButtonText}>
                    <dl>
                        <dt>{getDictionaryString("header.miniBasket.purchase")}</dt>
                        <dd>
                            {priceData && priceData.subtotal
                                ? formatPrice(priceData?.subtotal)
                                : "0,00"}
                        </dd>
                        <dt className={S.green}>
                            {" "}
                            {getDictionaryString("header.miniBasket.delivery")}
                        </dt>
                        <dd className={S.green}>
                            {priceData && priceData.shipping
                                ? formatPrice(priceData?.shipping)
                                : "0,00"}
                        </dd>
                    </dl>
                </span>
            </button>
        </div>
    );
});

DA_ServiceButtonsB2c.displayName = "DA_ServiceButtonsB2c";
